import Swiper from 'swiper';
import { Navigation, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

/**
 * Construction Slider
 * Кастомний слайдер для темпів будівництва на базі Swiper.js
 */

export default function initConstruction() {
  // Construction timeline aspects switching
  (() => {
    const aspectButtons = document.querySelectorAll('.construction-timeline__aspects-nav-button');
    const slideGroups = document.querySelectorAll('.construction-slider__slide-group');

    aspectButtons.forEach(button => {
      let isProcessing = false;

      button.addEventListener('click', function (e) {
        // Prevent double clicks
        if (this.classList.contains('active') || isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        isProcessing = true;
        const selectedAspect = this.dataset.aspect;

        // Remove active class from all buttons
        aspectButtons.forEach(btn => btn.classList.remove('active'));
        // Add active class to clicked button
        this.classList.add('active');

        // Show selected slide group
        const selectedGroup = document.querySelector(`.construction-slider__slide-group[data-aspect="${selectedAspect}"]`);
        if (selectedGroup) {
          const slider = selectedGroup.closest('.construction-slider');
          if (slider && slider.constructionSliderInstance) {
            const instance = slider.constructionSliderInstance;

            // Stop autoplay first
            instance.stopAutoplay();

            // Check if already showing this group
            if (instance.currentSlideGroup === selectedGroup && instance.swiper && instance.swiper.slides.length > 0) {
              instance.swiper.update();
              instance.updatePagination();
              isProcessing = false;
              return;
            }

            // Destroy existing Swiper before switching
            if (instance.swiper) {
              instance.swiper.destroy(true, true);
              instance.swiper = null;
            }

            // Set current group first
            instance.currentSlideGroup = selectedGroup;

            // Hide all slide groups with fade out
            slideGroups.forEach(group => {
              if (group !== selectedGroup) {
                // Fade out first
                group.style.opacity = '0';
                group.style.visibility = 'hidden';

                // Hide after transition completes (200ms)
                setTimeout(() => {
                  if (group !== selectedGroup && group.style.opacity === '0') {
                    group.style.display = 'none';
                  }
                }, 200);
              }
            });

            // Show selected group first (but keep it invisible)
            selectedGroup.style.display = 'flex';
            selectedGroup.style.opacity = '0';
            selectedGroup.style.visibility = 'hidden';

            // Ensure wrapper and slides are visible
            const wrapper = selectedGroup.querySelector('.swiper-wrapper');
            const slides = selectedGroup.querySelectorAll('.swiper-slide');

            if (wrapper && slides.length > 0) {
              wrapper.style.display = 'flex';
              slides.forEach(slide => {
                slide.style.display = '';
                slide.style.visibility = '';
                slide.style.width = '';
              });

              // Force reflow
              void selectedGroup.offsetHeight;
              void wrapper.offsetHeight;

              // Initialize Swiper BEFORE fade in to prevent flicker
              instance.initializeSwiper();

              // Fade in after Swiper is initialized
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  selectedGroup.style.opacity = '1';
                  selectedGroup.style.visibility = 'visible';
                });
              });

              // Reset processing flag after transition
              setTimeout(() => {
                isProcessing = false;
              }, 250);
            } else {
              isProcessing = false;
            }
          } else {
            // If no instance exists, just show the group
            selectedGroup.style.display = 'flex';
            isProcessing = false;
          }
        } else {
          isProcessing = false;
        }
      });
    });
  })();

  // Кастомний слайдер темпів будівництва на базі Swiper
  class ConstructionSlider {
    constructor(element) {
      this.element = element;
      this.swiper = null;
      this.isAutoplay = false;
      this.autoplayInterval = null;
      this.autoplayDelay = 3000;
      this.currentSlideGroup = null;
      this.slideshowBtn = null;
      this.pauseIconUrl = null; // Збережемо URL іконки паузи

      this.init();
    }

    init() {
      // Find visible slide group
      const allGroups = this.element.querySelectorAll('.construction-slider__slide-group');
      this.currentSlideGroup = Array.from(allGroups).find(g => {
        const style = window.getComputedStyle(g);
        return style.display !== 'none' && style.opacity !== '0';
      });

      if (!this.currentSlideGroup && allGroups.length > 0) {
        this.currentSlideGroup = allGroups[0];
        this.currentSlideGroup.style.display = 'flex';
        this.currentSlideGroup.style.opacity = '1';
        this.currentSlideGroup.style.visibility = 'visible';
      }

      if (!this.currentSlideGroup) return;

      this.initializeSwiper();
      this.bindEvents();
    }

    initializeSwiper() {
      if (!this.currentSlideGroup) return;

      // Destroy existing Swiper if exists
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      // Get navigation elements
      const prevBtn = this.element.querySelector('.construction-slider__nav-btn--prev');
      const nextBtn = this.element.querySelector('.construction-slider__nav-btn--next');
      this.slideshowBtn = this.element.querySelector('.construction-timeline__slideshow-btn');

      // Зберігаємо URL іконки паузи (slideshow.svg)
      if (this.slideshowBtn && !this.pauseIconUrl) {
        // URL до slideshow.svg
        const themeUri = window.location.origin + '/wp-content/themes/blago-main-site-api';
        this.pauseIconUrl = themeUri + '/assets/img/cbb/slideshow.svg';
      }

      // Initialize Swiper
      this.swiper = new Swiper(this.currentSlideGroup, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        loop: false,
        allowTouchMove: true,
        autoplay: false, // Вимикаємо вбудований autoplay, використовуємо кастомний
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn,
        },
        on: {
          init: () => {
            this.updatePagination();
            this.bindSlideEvents();
          },
          slideChange: () => {
            this.updatePagination();
            // Restart progress animation if autoplay is active
            if (this.isAutoplay) {
              this.startProgressAnimation();
            }
          },
        },
        modules: [Navigation, Keyboard],
      });

      // Create custom pagination
      this.createPagination();
    }

    createPagination() {
      const paginationContainer = this.element.querySelector('.construction-slider__pagination');
      if (!paginationContainer) return;

      paginationContainer.innerHTML = '';

      const slides = this.currentSlideGroup.querySelectorAll('.construction-slider__slide');

      slides.forEach((_, index) => {
        const bullet = document.createElement('div');
        bullet.className = 'construction-slider__pagination-bullet';
        if (index === this.swiper.activeIndex) {
          bullet.classList.add('construction-slider__pagination-bullet--active');
        }
        bullet.addEventListener('click', () => this.swiper.slideTo(index));
        paginationContainer.appendChild(bullet);
      });
    }

    updatePagination() {
      if (!this.swiper) return;

      const bullets = this.element.querySelectorAll('.construction-slider__pagination-bullet');
      const activeIndex = this.swiper.activeIndex;

      bullets.forEach((bullet, index) => {
        const isActive = index === activeIndex;

        if (isActive) {
          bullet.classList.add('construction-slider__pagination-bullet--active');
          if (this.isAutoplay) {
            bullet.classList.add('play');
            bullet.classList.remove('pause');
            // Create progress div if not exists
            let progressDiv = bullet.querySelector('div');
            if (!progressDiv) {
              progressDiv = document.createElement('div');
              bullet.appendChild(progressDiv);
            }
            // Reset and restart animation
            progressDiv.style.animation = 'none';
            progressDiv.offsetHeight; // Force reflow
            progressDiv.style.animation = 'fillProgress 3s linear forwards';
          } else {
            bullet.classList.add('pause');
            bullet.classList.remove('play');
            const progressDiv = bullet.querySelector('div');
            if (progressDiv) {
              progressDiv.style.animation = 'none';
            }
          }
        } else {
          bullet.classList.remove('construction-slider__pagination-bullet--active', 'play', 'pause');
          const progressDiv = bullet.querySelector('div');
          if (progressDiv) {
            progressDiv.style.animation = 'none';
            progressDiv.remove();
          }
        }
      });
    }

    bindEvents() {
      // Slideshow button
      if (this.slideshowBtn) {
        this.slideshowBtn.addEventListener('click', () => this.toggleAutoplay());
      }

      // Keyboard navigation for space bar
      this.element.addEventListener('keydown', (e) => {
        if (e.key === ' ') {
          e.preventDefault();
          this.toggleAutoplay();
        }
      });
    }

    bindSlideEvents() {
      // Click on slide to open lightbox
      const slides = this.currentSlideGroup.querySelectorAll('.construction-slider__slide');
      slides.forEach(slide => {
        slide.addEventListener('click', () => this.openLightbox(slide));
      });
    }

    toggleAutoplay() {
      if (!this.swiper || !this.slideshowBtn) return;

      const icon = this.slideshowBtn.querySelector('.construction-timeline__slideshow-btn-icon');

      // Toggle state
      if (this.isAutoplay) {
        // Stop autoplay - показуємо іконку play (#cbb-gallery-play)
        this.stopAutoplay();
        this.slideshowBtn.classList.remove('playing');
        if (icon) {
          // Показуємо іконку play
          icon.innerHTML = '<svg width="16" height="17"><use xlink:href="#cbb-gallery-play"></use></svg>';
        }
        this.updatePagination();
      } else {
        // Start autoplay - показуємо іконку паузи (slideshow.svg)
        this.startAutoplay();
        this.slideshowBtn.classList.add('playing');
        if (icon && this.pauseIconUrl) {
          icon.innerHTML = `<img src="${this.pauseIconUrl}" alt="" width="16" height="16">`;
        }
      }
    }

    startAutoplay() {
      if (!this.swiper) return;

      // Stop existing autoplay if any
      this.stopAutoplay();

      // Set state to true first
      this.isAutoplay = true;

      // Start custom autoplay with progress animation
      this.startProgressAnimation();

      this.autoplayInterval = setInterval(() => {
        if (this.swiper.isEnd) {
          this.swiper.slideTo(0);
        } else {
          this.swiper.slideNext();
        }
        // Animation will restart in slideChange handler
      }, this.autoplayDelay);

      this.updatePagination();
    }

    startProgressAnimation() {
      // Remove and re-add animation to restart it
      const activeBullet = this.element.querySelector('.construction-slider__pagination-bullet--active');
      if (activeBullet && this.isAutoplay) {
        const progressDiv = activeBullet.querySelector('div');
        if (progressDiv) {
          // Reset animation
          progressDiv.style.animation = 'none';
          // Force reflow
          progressDiv.offsetHeight;
          // Restart animation
          progressDiv.style.animation = 'fillProgress 3s linear forwards';
        }
      }
    }

    stopAutoplay() {
      if (!this.swiper) return;

      // Clear interval first
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }

      // Set state to false
      this.isAutoplay = false;

      // Stop progress animation
      const activeBullet = this.element.querySelector('.construction-slider__pagination-bullet--active');
      if (activeBullet) {
        const progressDiv = activeBullet.querySelector('div');
        if (progressDiv) {
          progressDiv.style.animation = 'none';
        }
      }

      this.updatePagination();
    }

    switchToGroup(newGroup) {
      this.currentSlideGroup = newGroup;
      this.stopAutoplay();
      this.initializeSwiper();
    }

    openLightbox(slide) {
      // Get photo data from slide
      const photoUrl = slide.dataset.photoUrl;

      if (!photoUrl) return;

      // Get all photos from current aspect for gallery
      const allSlides = this.currentSlideGroup.querySelectorAll('.construction-slider__slide');
      const galleryItems = [];

      allSlides.forEach((s, index) => {
        const url = s.dataset.photoUrl;
        const caption = s.dataset.photoCaption || '';
        if (url) {
          galleryItems.push({
            src: url,
            caption: caption
          });
        }
      });

      // Find current index
      const currentIndex = Array.from(allSlides).indexOf(slide);

      // Create temporary container for Fancybox in body before footer (only when needed)
      let fancyboxContainer = document.getElementById('construction-fancybox-container');
      if (!fancyboxContainer) {
        fancyboxContainer = document.createElement('div');
        fancyboxContainer.id = 'construction-fancybox-container';
        fancyboxContainer.style.display = 'none';
        fancyboxContainer.setAttribute('aria-hidden', 'true');

        // Insert before footer
        const footer = document.querySelector('footer');
        if (footer && footer.parentNode) {
          footer.parentNode.insertBefore(fancyboxContainer, footer);
        } else {
          document.body.appendChild(fancyboxContainer);
        }
      }

      // Clear existing items
      fancyboxContainer.innerHTML = '';

      // Create Fancybox items
      galleryItems.forEach((item, index) => {
        const itemEl = document.createElement('a');
        itemEl.href = item.src;
        itemEl.setAttribute('data-fancybox', 'construction-gallery');
        itemEl.setAttribute('data-caption', item.caption);
        if (index === currentIndex) {
          itemEl.classList.add('fancybox-current');
        }
        fancyboxContainer.appendChild(itemEl);
      });

      // Initialize Fancybox only when clicking
      if (typeof Fancybox !== 'undefined') {
        // Unbind previous instance if exists
        try {
          Fancybox.unbind('[data-fancybox="construction-gallery"]');
        } catch (e) {
          // Ignore if not bound
        }

        // Bind Fancybox to gallery items
        Fancybox.bind('[data-fancybox="construction-gallery"]', {
          infinite: true,
          loop: false,
          Toolbar: {
            display: {
              left: ['infobar'],
              middle: [],
              right: ['slideshow', 'download', 'thumbs', 'close']
            }
          },
          on: {
            destroy: () => {
              // Clean up after Fancybox closes
              setTimeout(() => {
                if (fancyboxContainer) {
                  fancyboxContainer.innerHTML = '';
                }
              }, 100);
            }
          }
        });

        // Wait a bit for Fancybox to initialize, then trigger click
        requestAnimationFrame(() => {
          const currentItem = fancyboxContainer.querySelector('.fancybox-current');
          if (currentItem) {
            currentItem.click();
          } else {
            // Fallback: click first item
            const firstItem = fancyboxContainer.querySelector('[data-fancybox="construction-gallery"]');
            if (firstItem) {
              firstItem.click();
            }
          }
        });
      }
    }

    destroy() {
      this.stopAutoplay();
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }
    }
  }

  function initializeConstructionSlider(element) {
    if (element && !element.constructionSliderInstance) {
      element.constructionSliderInstance = new ConstructionSlider(element);
    }
  }

  // Initialize all construction sliders when DOM is ready
  function initConstructionSliders() {
    document.querySelectorAll('.construction-slider').forEach(initializeConstructionSlider);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initConstructionSliders);
  } else {
    initConstructionSliders();
  }

  // Export for global access if needed
  window.ConstructionSlider = ConstructionSlider;
  window.initializeConstructionSlider = initializeConstructionSlider;
}

